import PropertyDropdown from './PropertyDropdown';
import ToggleGroup from './ToggleGroup';

type Option = { caption: string; value: string | null };

type Props = {
  values: Option[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  ariaLabel: string;
};

/** A choice this small reads faster as segments than as a closed menu. */
const MAX_SEGMENTS = 3;
const MAX_SEGMENT_LABEL = 12;

export const fitsSegments = (values: Option[]) =>
  values.length > 1 &&
  values.length <= MAX_SEGMENTS &&
  values.every(v => v.value !== null && v.caption.length <= MAX_SEGMENT_LABEL);

/**
 * A side panel's pick-one field: up to three short answers are a gold-tone
 * segmented control, so every answer is on screen and one click away; more,
 * or longer, falls back to the panel dropdown (docs/design-system.md § "Panel
 * Fields": choosing a control). Fit is the first user: Cover / Contain /
 * Black Frame were a closed dropdown (PLAT-12137).
 */
const PanelChoiceField = ({ values, value, onChange, ariaLabel }: Props) => {
  if (fitsSegments(values)) {
    return (
      <ToggleGroup
        ariaLabel={ariaLabel}
        tone="accent"
        options={values.map(v => ({ id: v.value ?? '', label: v.caption }))}
        value={value ?? ''}
        onChange={onChange}
      />
    );
  }
  return (
    <PropertyDropdown
      ariaLabel={ariaLabel}
      values={values}
      value={value ?? ''}
      defaultValue={value}
      onChange={onChange}
    />
  );
};

export default PanelChoiceField;
